import { useState } from 'react'
import { Modal, ActionIcon, Button, SegmentedControl } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { Plus, Minus } from '@untitled-ui/icons-react'
import ComponentSelect2 from '@/components/ui/ComponentSelect2'
import SKU from '@/components/ui/SKU'
import { useAxiosApi } from '@/hooks/useAxiosApi'

const ATELIER_PARIS_LOCATION_ID = 6

export default function AddStockMovementModal({ serviceCase, opened, onClose, onSuccess }) {
    const [selectedComponent, setSelectedComponent] = useState(null)
    const [direction, setDirection] = useState('out')
    const [quantity, setQuantity] = useState(1)
    const [loading, setLoading] = useState(false)
    const { requestPost } = useAxiosApi()

    const bundleComponents = (serviceCase.bundle_components || []).toSorted((a, b) => {
        const nameA = a.category_name ? `${a.category_name} – ${a.name}` : (a.name || '')
        const nameB = b.category_name ? `${b.category_name} – ${b.name}` : (b.name || '')
        return nameA.localeCompare(nameB)
    })

    const suggestedComponents = (serviceCase.suggested_components || []).toSorted((a, b) => {
        const nameA = a.category_name ? `${a.category_name} – ${a.name}` : (a.name || '')
        const nameB = b.category_name ? `${b.category_name} – ${b.name}` : (b.name || '')
        return nameA.localeCompare(nameB)
    })

    const handleClose = () => {
        setSelectedComponent(null)
        setDirection('out')
        setQuantity(1)
        onClose()
    }

    const handleSubmit = async () => {
        if (!selectedComponent || !quantity) return
        const evolution = direction === 'in' ? quantity : -quantity
        setLoading(true)
        try {
            await requestPost(`/components/${selectedComponent.id}/edit-inventory`, {
                location_id: ATELIER_PARIS_LOCATION_ID,
                evolution,
                reason: direction === 'out' ? 'service_case_out' : 'service_case_in',
                service_case_id: serviceCase.id,
            })
            notifications.show({
                title: 'Mouvement enregistré',
                message: `${evolution > 0 ? '+' : ''}${evolution} sur ${selectedComponent.display_name}`,
                color: 'green',
            })
            handleClose()
            onSuccess?.()
        } catch (e) {
            notifications.show({
                title: 'Erreur',
                message: e.response?.data?.error_message || 'Erreur inconnue',
                color: 'red',
            })
        }
        setLoading(false)
    }

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            title="Ajouter un mouvement de stock"
            size="xl"
            centered
        >
            <div className="flex flex-col gap-4">
                {!selectedComponent ? (
                    <>
                        {suggestedComponents.length > 0 && (
                            <div className="flex flex-col gap-1.5">
                                <span className="text-sm font-medium text-gray-500">
                                    Composants du produit
                                </span>
                                <div className="max-h-[320px] overflow-y-auto border rounded-lg divide-y">
                                    {suggestedComponents.map((c) => (
                                        <button
                                            key={c.id}
                                            type="button"
                                            className="w-full text-left text-sm px-3 py-2.5 hover:bg-gray-50 transition-colors cursor-pointer flex items-center gap-2"
                                            onClick={() => setSelectedComponent(c)}
                                        >
                                            <SKU component={c} size="sm" />
                                            <span className="truncate">{c.category_name ? `${c.category_name} – ${c.name}` : c.name}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {bundleComponents.length > 0 && (
                            <div className="flex flex-col gap-1.5">
                                <span className="text-sm font-medium text-gray-500">
                                    Composants du bundle
                                </span>
                                <div className="max-h-[320px] overflow-y-auto border rounded-lg divide-y">
                                    {bundleComponents.map((c) => (
                                        <button
                                            key={c.id}
                                            type="button"
                                            className="w-full text-left text-sm px-3 py-2.5 hover:bg-gray-50 transition-colors cursor-pointer flex items-center gap-2"
                                            onClick={() => setSelectedComponent(c)}
                                        >
                                            <SKU component={c} size="sm" />
                                            <span className="truncate">{c.category_name ? `${c.category_name} – ${c.name}` : c.name}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        <ComponentSelect2
                            label="Ou rechercher un composant"
                            placeholder="Rechercher..."
                            onSelect={(result) => setSelectedComponent({ id: result.id, display_name: result.name })}
                        />
                    </>
                ) : (
                    <>
                        <div className="flex items-center justify-between gap-2 border rounded-lg px-3 py-2 bg-gray-50">
                            <span className="text-sm font-medium truncate">{selectedComponent.display_name}</span>
                            <Button
                                variant="subtle"
                                size="compact-xs"
                                onClick={() => setSelectedComponent(null)}
                            >
                                Changer
                            </Button>
                        </div>

                        <SegmentedControl
                            value={direction}
                            onChange={setDirection}
                            fullWidth
                            data={[
                                { label: 'Utilisation', value: 'out' },
                                { label: 'Remise en stock', value: 'in' },
                            ]}
                        />

                        <div className="flex items-center justify-center gap-3">
                            <ActionIcon
                                variant="default"
                                size={42}
                                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                disabled={quantity <= 1}
                            >
                                <Minus width={18} height={18} />
                            </ActionIcon>
                            <span className="text-2xl font-semibold tabular-nums w-10 text-center">
                                {quantity}
                            </span>
                            <ActionIcon
                                variant="default"
                                size={42}
                                onClick={() => setQuantity(quantity + 1)}
                            >
                                <Plus width={18} height={18} />
                            </ActionIcon>
                        </div>

                        <div className="text-sm text-gray-500">
                            Lieu : Atelier Paris
                        </div>

                        <Button
                            fullWidth
                            onClick={handleSubmit}
                            loading={loading}
                            disabled={!quantity}
                            color={direction === 'out' ? 'red' : 'green'}
                        >
                            {direction === 'out' ? `Retirer ${quantity || ''}` : `Ajouter ${quantity || ''}`}
                        </Button>
                    </>
                )}
            </div>
        </Modal>
    )
}
